import { Befehlsnachricht, BfnStatus } from '@/models/Befehlsnachricht'
import { convertBefehlModelToDto } from '@/apis/converters/befehl-dto-converter'
import {
  BefehlsnachrichtHash1Parts,
  BefehlsnachrichtHash2Parts,
  verifyBefehlsnachrichtHash
} from './hash'
import {
  verifyEindeutigeKennungHash,
  verifyStandortHash,
  verifyStatusHash
} from '@repo/ui'

/**
 * Verifies if all hashes in Befehlsnachricht are valid.
 *
 * @see verifyBefehlsnachrichtHash
 */
export async function verifyBefehlsnachricht(
  befehlsnachricht: Befehlsnachricht
) {
  // Hash2 contains befehle data that is only visible for status values after BfnStatus.STANDORTEINGABE_AKZEPTIERT (inclusive)
  const ignoreHash2 = [
    BfnStatus.GELOESCHT,
    BfnStatus.ERSTABRUF_GESTARTET,
    BfnStatus.STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH,
    BfnStatus.STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH,
    BfnStatus.STANDORTABGLEICH_FEHLGESCHLAGEN
  ].includes(befehlsnachricht.status)

  const isHash1Valid = await verifyBefehlsnachrichtHash(
    {
      id: befehlsnachricht.id,
      zugnummer: befehlsnachricht.zugnummer,
      artDerFahrt: befehlsnachricht.artDerFahrt,
      arbeitsplatzbezeichnung: befehlsnachricht.fdlArbeitsplatz,
      arbeitsplatzOrt: befehlsnachricht.fdlArbeitsplatzOrt,
      externalUserDisplayName: befehlsnachricht.fdlName
    } as BefehlsnachrichtHash1Parts,
    befehlsnachricht.hash1
  )

  const isHash2Valid =
    ignoreHash2 ||
    (befehlsnachricht.hash2 !== null &&
      (await verifyBefehlsnachrichtHash(
        {
          id: befehlsnachricht.id,
          zugnummer: befehlsnachricht.zugnummer,
          artDerFahrt: befehlsnachricht.artDerFahrt,
          arbeitsplatzbezeichnung: befehlsnachricht.fdlArbeitsplatz,
          arbeitsplatzOrt: befehlsnachricht.fdlArbeitsplatzOrt,
          externalUserDisplayName: befehlsnachricht.fdlName,
          befehle: await Promise.all(
            befehlsnachricht.befehle.map((befehl) =>
              convertBefehlModelToDto(befehl)
            )
          )
        } as BefehlsnachrichtHash2Parts,
        befehlsnachricht.hash2
      )))

  const isEindeutigeKennungHashValid = await verifyEindeutigeKennungHash(
    befehlsnachricht.id,
    befehlsnachricht.eindeutigeKennungHash,
    befehlsnachricht.eindeutigeKennung
  )
  const isStandortHashValid = await verifyStandortHash(
    befehlsnachricht.id,
    befehlsnachricht.standortHash,
    befehlsnachricht.standort
  )
  const isStatusHashValid = await verifyStatusHash(
    befehlsnachricht.id,
    befehlsnachricht.statusTfHash,
    befehlsnachricht.visuellerStatus,
    befehlsnachricht.status,
    befehlsnachricht.statusTimestamps
  )

  return (
    isHash1Valid &&
    isHash2Valid &&
    isEindeutigeKennungHashValid &&
    isStandortHashValid &&
    isStatusHashValid
  )
}
